import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, clearError } from '../store/slices/authSlice';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://increment.proactive.co.in/api';

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, user } = useSelector(s => s.auth);
  const [form, setForm]     = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => { if (user) navigate('/increment-cycles'); }, [user, navigate]);
  useEffect(() => { if (error) toast.error(error); }, [error]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearError());
    dispatch(login(form));
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${API_BASE}/auth/microsoft/`;
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <div style={{
        flex: 1, background: 'var(--navy)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '48px 56px',
        backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(200,50,160,0.15) 0%, transparent 60%), radial-gradient(circle at 80% 20%, rgba(200,50,160,0.08) 0%, transparent 50%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 48 }}>
          <div style={{ width: 44, height: 44, background: 'var(--magenta)', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div>
            <div style={{ color: 'var(--white)', fontWeight: 800, fontSize: 22, letterSpacing: 2 }}>
              PRO<span style={{ color: 'var(--magenta)' }}>A</span>CTIVE
            </div>
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, letterSpacing: 1 }}>
              ENHANCING DIGITAL EXPERIENCE
            </div>
          </div>
        </div>
        <h1 style={{ color: 'var(--white)', fontSize: 32, fontWeight: 700, lineHeight: 1.2, marginBottom: 16 }}>
          Increment<br />Management System
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, maxWidth: 360, lineHeight: 1.7 }}>
          Seamlessly manage increments, inspire performance, and future-proof your workforce with a complete increment management solution.
        </p>
        <div style={{ position: 'absolute', bottom: 48, left: 40, opacity: 0.06 }}>
          {[180, 130, 80].map((s, i) => (
            <div key={i} style={{
              position: 'absolute', width: s, height: s, borderRadius: '50%',
              border: '2px solid var(--white)',
              transform: `translate(${i * -15}px, ${i * -15}px)`,
            }} />
          ))}
        </div>
      </div>

      <div style={{ width: 440, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, background: 'var(--white)' }}>
        <div style={{ width: '100%', maxWidth: 360 }}>
          <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 6 }}>Welcome Back</h2>
          <p style={{ color: 'var(--grey-400)', fontSize: 13, marginBottom: 32 }}>Sign in to your account to continue</p>

          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={loading}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 10, padding: '11px 16px', marginBottom: 20,
              border: '1.5px solid #d1d5db', borderRadius: 8, background: '#fff',
              cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151',
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#9ca3af'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff';    e.currentTarget.style.borderColor = '#d1d5db'; }}
          >
            <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
              <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            <span style={{ color: '#9ca3af', fontSize: 12 }}>or sign in with email</span>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="form-group">
              <label className="form-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Email
              </label>
              <input className="form-input" type="email" placeholder="you@company.com"
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required autoFocus />
            </div>

            <div className="form-group">
              <label className="form-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showPwd ? 'text' : 'password'} placeholder="••••••••"
                  value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required style={{ paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPwd(v => !v)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--grey-400)',
                }}>
                  {showPwd
                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 15 }}>
              {loading ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Signing in…</> : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
